import type { AppMode } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { isEmbeddedMode } from '@jane/shared/util';

import { useHeadlessApp } from '../headlessAppProvider/headlessAppProvider';

interface EcommAppContextProps {
  appMode: AppMode;
}

interface EcommAppContextValues {
  appMode: AppMode;
  isEmbed: boolean;
  isIframe: boolean;
}

const EcommAppContext = createContext<EcommAppContextValues>(
  {} as EcommAppContextValues
);

export const EcommAppProvider = ({
  appMode,
  children,
}: EcommAppContextProps & { children: ReactNode }) => {
  const { options } = useHeadlessApp();
  const actualAppMode = options?.bloomUserExperience ? 'bloom' : appMode;

  const isIframe = ['embedded', 'operatorEmbed'].includes(appMode);
  const isEmbed = isEmbeddedMode(appMode);

  const value = useMemo(() => {
    return { appMode: actualAppMode, isEmbed, isIframe };
  }, [actualAppMode, isEmbed, isIframe]);

  return (
    <EcommAppContext.Provider value={value}>
      {children}
    </EcommAppContext.Provider>
  );
};

export const useSafeEcommApp = () => useContext(EcommAppContext);

export const useEcommApp = () => {
  const context = useContext(EcommAppContext);

  if (!Object.keys(context).length) {
    throw new Error(
      'useEcommApp must be used within an EcommAppProvider. Use useSafeEcommApp if used within a component shared with non-Ecomm apps'
    );
  }

  return context;
};
